import {React, useState, useEffect} from 'react';
import axios from 'axios';
import '../../App.css'
import { Grid, TextField, Box, Button, Paper,Stack, FormControl, FormControlLabel, Checkbox,Typography} from '@mui/material';
import {Preview, CropRotate, ArrowForwardIos} from '@mui/icons-material';
import * as employeeService from '../../services/employeeService';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red' , marginLeft: '3px' }}>*</span>
  </span>
);

const options = employeeService.getDepartmentCollection();

const initialFValues={
  id: 0,
  salaryLimit: '',
  esicPercentage: '',
  isActive: false,
  effectiveDateFrom: new Date().toISOString().split('T')[0],
  effectiveDateTo: '',
  companyContriPercentage: '',
  comContriEffectiveDateFrom: new Date().toISOString().split('T')[0],
}

function ESIC_Configuration(){

  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [notify, setNotify] = useState({isOpen: false, message: '', type: ''});
  const navigate = useNavigate();

  const validate = () => {
    let temp = {}
    temp.salaryLimit = values.salaryLimit ? "" : "This field is required.";
    temp.esicPercentage = values.esicPercentage ? "" : "This field is required.";
    temp.companyContriPercentage = values.companyContriPercentage ? "" : "This field is required.";

    setErrors({
      ...temp
    })

    return Object.values(temp).every(x => x === "")
  }

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  }


  function handleInputChange(e){
    const {name, value} = e.target;
    setValues({
      ...values,
      [name]: value
    })
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  }

  const convertToDefEventPara = (name, value) => ({
    target: {
      name: name,
      value: value
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault();
      if(validate()) {
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/esic-configure/save`, values)
      .then(function (response) {
        if (response.status === 200) {
          setNotify({
            isOpen: true,
            message: response.data.message,
            type: 'success'
          });
          setDisabled(true);
        }
      })
      .catch(function (e) {
        if(e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          },1000);
        } else if(e.response.status === 422) {
          setNotify({
            isOpen: true,
            message: e.response.data.error_message.sqlMessage,
            type: 'error'
          });
        }
      });
    }
  }

  async function handleGetData() {
    try {
      const employees = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/esic-configure/get-data`);
      setEmployees(employees.data);

      setValues({
        ...values,
        salaryLimit: employees.data[0].Esic_Limit,
        esicPercentage: employees.data[0].EsicBasicPer,
        isActive: employees.data[0].Status,
        effectiveDateFrom: employees.data[0].EffectiveFrom.split('T')[0],
        effectiveDateTo: employees.data[0].EffectiveTo.split('T')[0],
        companyContriPercentage: employees.data[0].companyContriPercentage,
        comContriEffectiveDateFrom: employees.data[0].comContriEffectiveDateFrom.split('T')[0],
      });

    } catch (e) {
      if(e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        },1000);
      }
    }
  }

  useEffect(() => {
    handleGetData();
  }, []);

  // Common styles for all text fields
  const textFieldStyles = {
    width: '100%',
    '& .MuiInputBase-root': {
      height: '40px' // Consistent height for all fields
    }
  };

  return(
    <Box sx={{display: "flex" ,justifyContent: "center", margin: "10px", padding: "50px", fontWeight: "500px"}}>
      <Paper elevation={5} sx={{width: "500px"}}>
        <Grid className="form-title" container spacing={2}>
          <Grid item> <Typography variant="h5" sx={{textAlign: "center", margin: 5}}>ESIC Configuration</Typography> </Grid>
          <Grid item> <CropRotate  style={{marginLeft: "-30px"}} sx={{width: 45, height: 45}}/> </Grid>
        </Grid>
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{margin: "30px", marginTop: "-7px"}}>

            <Button  variant="outlined" color="success" onClick={handleGetData} startIcon={<Preview sx={{width: "30px", height: "30px"}}/>} >Get Current Data</Button>
            
            {/* Status Checkbox */}
            <Stack className="form-input-row" direction="row" spacing={2} style={{marginTop: '20px'}}>
              <FormControl sx={{ width: '100%' }}>
                <FormControlLabel
                  control={<Checkbox
                          name="isActive"
                          color="primary"
                          checked={values.isActive}
                          onChange={ e => handleInputChange(convertToDefEventPara("isActive", e.target.checked))}
                          />}
                          label="Status?"
                          />
              </FormControl>
            </Stack>
            
            {/* First Row - Basic ESIC Configuration */}
            <Stack className="form-input-row" direction="row" spacing={2} style={{marginTop: '20px'}}>
              <TextField
                variant="outlined"
                name="salaryLimit"
                label={<RequiredLabel label="Salary Limit (&#8377;)" />}
                size="small"
                sx={textFieldStyles}
                value={values.salaryLimit}
                onChange={handleInputChange}
                {...(errors.salaryLimit && {error:true, helperText: errors.salaryLimit} )}
              />
              <TextField
                variant="outlined"
                name="esicPercentage"
                label={<RequiredLabel label="ESIC %" />}
                size="small"
                sx={textFieldStyles}
                value={values.esicPercentage}
                onChange={handleInputChange}
                {...(errors.esicPercentage && {error:true, helperText: errors.esicPercentage} )}
              />
            </Stack>

            {/* Second Row - Company Contribution and Date */}
            <Stack className="form-input-row" direction="row" spacing={2} style={{marginTop: '20px'}}>
              <TextField
                variant="outlined"
                name="companyContriPercentage"
                label={<RequiredLabel label="Company Contribution %" />}
                size="small"
                sx={textFieldStyles}
                value={values.companyContriPercentage}
                onChange={handleInputChange}
                {...(errors.companyContriPercentage && {error:true, helperText: errors.companyContriPercentage} )}
              />
              <TextField
                variant="outlined"
                className="date-picker"
                type="date"
                label="Effective Date From"
                name="effectiveDateFrom"
                size="small"
                sx={textFieldStyles}
                value={values.effectiveDateFrom}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Stack>

            {/* Third Row - Company Contribution Date */}
            <Stack className="form-input-row" direction="row" spacing={2} style={{marginTop: '20px'}}>
              <TextField
                variant="outlined"
                className="date-picker"
                type="date"
                label="Company Contribution Eff. Date From"
                name="comContriEffectiveDateFrom"
                size="small"
                sx={textFieldStyles}
                value={values.comContriEffectiveDateFrom}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Stack>

              <Stack className="right-aligned form-btn-container">
                  <Button className="form-btn" variant="contained" style={{marginRight: '-29px', marginTop: '-25px', marginBottom: '-25px'}} endIcon={<ArrowForwardIos />} type="submit" disabled={disabled}>Save</Button>
              </Stack>

            </Stack>
        </form>
      </Paper>
      <Notification
            notify={notify}
            setNotify={setNotify} />
    </Box>
  )
}

export default ESIC_Configuration;